/*
  HydroJug theme runtime (vanilla JS). Gives the static header and drawer markup the behavior the live
  Hydrogen app has: promo bar rotation, mega menu, mobile drawer, search overlay with predictive results,
  cart drawer and /cart page backed by the Shopify Ajax Cart API + Section Rendering API.

  Public API (window.HJ):
    HJ.cart.add(items, opts)      items: [{id, quantity, properties?}] or [{handle, quantity, options?}]
                                  (handle: first available variant of /products/<handle>.js, or the one whose
                                  option values match `options`, an array of values or of {name, value}).
                                  Opens the drawer unless opts.open === false. Resolves with the add.js JSON.
    HJ.cart.change(lineKey, qty)  set a line's quantity (0 removes it)
    HJ.cart.update(body)          POST /cart/update.js with body (e.g. {discount: 'CODE'} or {updates: {...}})
    HJ.cart.refresh()             re-render drawer (+ /cart page) and header badge from the server
    HJ.cart.open() / close()      the cart drawer
    HJ.cart.get()                 the cart JSON (/cart.js)
    HJ.search.open() / close(), HJ.menu.open() / close(), HJ.mega.open() / close()
    HJ.formatMoney(cents)         with the shop's money format
    HJ.variantFor(handle, options) resolves a variant id from a product handle
  Events on window: 'hj:cart-updated' (detail: {count}), 'hj:cart-error' (detail: {message}),
  and the live site's own 'sb:open-cart', 'sb:open-search', 'sb:cart-count' are honored/emitted.
*/
(function () {
  'use strict';
  var HJ = (window.HJ = window.HJ || {});
  var routes = HJ.routes || {};
  var ROOT = (routes.root || '/').replace(/\/?$/, '/');
  var CART = routes.cart || '/cart';
  var SEARCH = routes.search || '/search';
  var DRAWER_SECTION = 'hj-cart-drawer';
  var PAGE_SECTION = 'hj-cart';
  var desktop = window.matchMedia('(min-width: 768px)');
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  /* ---------- money ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    format = format || HJ.money || '${{amount}}';
    function delimit(n, precision, thousands, decimal) {
      var fixed = (n / 100).toFixed(precision).split('.');
      var whole = fixed[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return whole + (fixed[1] ? decimal + fixed[1] : '');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return delimit(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(cents, 2, "'", '.');
        default: return delimit(cents, 2, ',', '.');
      }
    });
  }
  HJ.formatMoney = formatMoney;

  /* ---------- scroll lock (same technique as the live site: body fixed at the scroll offset) ---------- */
  var lockDepth = 0, lockSaved = null;
  function lockScroll() {
    if (lockDepth++) return;
    var b = document.body, y = window.scrollY, gap = window.innerWidth - document.documentElement.clientWidth;
    lockSaved = { y: y, css: b.getAttribute('style') };
    b.style.position = 'fixed'; b.style.top = -y + 'px'; b.style.left = '0'; b.style.right = '0';
    b.style.width = '100%'; b.style.overflow = 'hidden';
    if (gap > 0) b.style.paddingRight = (parseFloat(getComputedStyle(b).paddingRight) || 0) + gap + 'px';
  }
  function unlockScroll() {
    if (!lockDepth || --lockDepth) return;
    var b = document.body;
    if (lockSaved.css == null) b.removeAttribute('style'); else b.setAttribute('style', lockSaved.css);
    window.scrollTo({ top: lockSaved.y, left: 0, behavior: 'instant' });
  }

  /* ---------- one overlay at a time (cart, search, menu), like the live Aside context ---------- */
  var current = null; // {name, close}
  var overlays = {};
  function registerOverlay(name, openFn, closeFn) {
    overlays[name] = {
      open: function () {
        if (current && current.name === name) return;
        if (current) overlays[current.name].close();
        mega.close();
        current = { name: name, focus: document.activeElement };
        lockScroll();
        openFn();
      },
      close: function () {
        if (!current || current.name !== name) return;
        var prev = current.focus;
        current = null;
        closeFn();
        unlockScroll();
        if (prev && prev.focus && document.contains(prev)) { try { prev.focus({ preventScroll: true }); } catch (e) {} }
      }
    };
    return overlays[name];
  }

  /* ---------- header lookups ---------- */
  var header = $('header.sb-site-header');
  function cartButton() { return header && $('button[aria-label^="Cart"]', header); }

  /* ---------- cart badge ---------- */
  function setCount(n) {
    n = Number(n) || 0;
    var btn = cartButton();
    if (btn) {
      btn.setAttribute('aria-label', n ? 'Cart, ' + n + ' item' + (n === 1 ? '' : 's') : 'Cart');
      var wrap = $('.sb-site-header__icon-wrap', btn) || btn;
      var badge = $('.sb-site-header__count', wrap);
      if (n) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'sb-site-header__count';
          badge.setAttribute('aria-hidden', 'true');
          wrap.appendChild(badge);
        }
        badge.textContent = n > 99 ? '99+' : String(n);
      } else if (badge) badge.remove();
    }
    HJ.cartCount = n;
    window.dispatchEvent(new CustomEvent('sb:cart-count', { detail: { count: n } }));
  }

  /* ---------- cart drawer ---------- */
  var cartOverlay = $('[data-hj-cart-overlay]');
  var cartAside = $('[data-hj-cart-aside]');
  var cartUI = registerOverlay('cart', function () {
    if (!cartOverlay) return;
    cartOverlay.classList.add('expanded');
    var c = $('header .close', cartAside);
    if (c) setTimeout(function () { try { c.focus({ preventScroll: true }); } catch (e) {} }, 50);
  }, function () {
    if (cartOverlay) cartOverlay.classList.remove('expanded');
  });

  function sectionIds() {
    var ids = [DRAWER_SECTION];
    if ($('[data-hj-cart-page]')) ids.push(PAGE_SECTION);
    return ids;
  }
  function parse(html) {
    var t = document.createElement('template');
    t.innerHTML = html;
    return t.content;
  }
  function applySections(sections) {
    if (!sections) return false;
    var ok = false;
    var drawerHtml = sections[DRAWER_SECTION];
    if (drawerHtml && cartAside) {
      var frag = parse(drawerHtml);
      var wrap = frag.querySelector('.shopify-section');
      var scroll = $('.cart-scroll', cartAside), top = scroll ? scroll.scrollTop : 0;
      if (wrap) cartAside.innerHTML = wrap.innerHTML;
      else { cartAside.innerHTML = ''; cartAside.appendChild(frag); }
      var s2 = $('.cart-scroll', cartAside); if (s2) s2.scrollTop = top;
      var counter = $('[data-hj-count]', cartAside);
      if (counter) setCount(counter.getAttribute('data-hj-count'));
      ok = true;
    }
    var pageHtml = sections[PAGE_SECTION];
    var page = $('[data-hj-cart-page]');
    if (pageHtml && page) {
      var next = parse(pageHtml).querySelector('[data-hj-cart-page]');
      if (next) { page.innerHTML = next.innerHTML; ok = true; }
    }
    return ok;
  }
  function emitUpdated() {
    window.dispatchEvent(new CustomEvent('hj:cart-updated', { detail: { count: HJ.cartCount || 0 } }));
  }
  function emitError(message) {
    window.dispatchEvent(new CustomEvent('hj:cart-error', { detail: { message: message } }));
    if (window.console) console.warn('[hj cart]', message);
  }
  function busy(on) {
    if (cartAside) cartAside.toggleAttribute('aria-busy', !!on);
    var page = $('[data-hj-cart-page]');
    if (page) page.toggleAttribute('aria-busy', !!on);
  }
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok || data.status && Number(data.status) >= 400) {
          var err = new Error(data.description || data.message || 'Cart request failed (' + r.status + ')');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }
  function withSections(body) {
    body.sections = sectionIds().join(',');
    body.sections_url = window.location.pathname;
    return body;
  }
  function afterChange(data) {
    if (!applySections(data && data.sections)) return refresh().then(function () { return data; });
    emitUpdated();
    return data;
  }

  var productCache = {};
  function productJSON(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(ROOT + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('Product not found: ' + handle); return r.json(); })
        .catch(function (e) { delete productCache[handle]; throw e; });
    }
    return productCache[handle];
  }
  function variantFor(handle, options) {
    return productJSON(handle).then(function (p) {
      var vs = p.variants || [];
      var wanted = (options || []).map(function (o) { return typeof o === 'string' ? o : o && o.value; })
        .filter(function (v) { return v && v !== 'Default Title'; })
        .map(function (v) { return String(v).toLowerCase(); });
      var match = null;
      if (wanted.length) {
        match = vs.filter(function (v) {
          var have = (v.options || []).map(function (x) { return String(x).toLowerCase(); });
          return wanted.every(function (w) { return have.indexOf(w) >= 0; });
        });
        match = match.filter(function (v) { return v.available; })[0] || match[0] || null;
      }
      var v = match || vs.filter(function (x) { return x.available; })[0] || vs[0];
      if (!v) throw new Error('No variant for ' + handle);
      return v.id;
    });
  }
  HJ.variantFor = variantFor;

  function add(items, opts) {
    opts = opts || {};
    items = Array.isArray(items) ? items : [items];
    busy(true);
    // Live opens the bag on click and fills it in when the cart answers; waiting for add.js first left the store
    // looking unresponsive for 1-3 s.
    if (opts.open !== false) cartUI.open();
    return Promise.all(items.map(function (it) {
      var q = Math.max(1, parseInt(it.quantity, 10) || 1);
      var base = { quantity: q };
      if (it.properties) base.properties = it.properties;
      if (it.selling_plan) base.selling_plan = it.selling_plan;
      if (it.id) { base.id = Number(String(it.id).replace(/\D/g, '')); return base; }
      return variantFor(it.handle, it.options).then(function (id) { base.id = id; return base; });
    })).then(function (resolved) {
      return postJSON(CART + '/add.js', withSections({ items: resolved }));
    }).then(afterChange).then(function (data) {
      busy(false);
      return data;
    }, function (err) {
      busy(false);
      emitError(err.message);
      if (opts.open !== false) refresh();
      throw err;
    });
  }
  function change(key, quantity) {
    busy(true);
    return postJSON(CART + '/change.js', withSections({ id: String(key), quantity: Math.max(0, parseInt(quantity, 10) || 0) }))
      .then(afterChange)
      .then(function (d) { busy(false); return d; }, function (err) { busy(false); emitError(err.message); return refresh(); });
  }
  // Codes Shopify kept but cannot apply ({code, applicable:false} in the cart JSON): the same error row the cart
  // markup has, under every discount field (drawer and page). Liquid cannot see rejected codes.
  function showRejected(data) {
    var codes = ((data && data.discount_codes) || []).filter(function (c) { return !c.applicable; }).map(function (c) { return c.code; });
    $$('form[data-hj-discount] input[type="text"][name="discount"]').forEach(function (input) {
      var form = input.form, next = form.nextElementSibling;
      if (next && next.hasAttribute('data-hj-rejected')) next.remove();
      if (!codes.length) return;
      var row = document.createElement('form');
      row.method = 'post'; row.action = CART + '/update';
      row.setAttribute('data-hj-cart-form', ''); row.setAttribute('data-hj-discount', ''); row.setAttribute('data-hj-rejected', '');
      row.innerHTML = '<input type="hidden" name="discount" value=""><div class="cart-code-error-row"><p id="' + input.id + '-error" class="cart-code-error" role="alert"></p><button type="submit" class="cart-code-remove" aria-label="Clear invalid discount code">Clear</button></div>';
      row.querySelector('p').textContent = codes.join(', ') + ' can\u2019t be applied to this cart.';
      input.setAttribute('aria-describedby', input.id + '-error');
      form.parentNode.insertBefore(row, form.nextSibling);
    });
    return data;
  }
  function update(body) {
    busy(true);
    return postJSON(CART + '/update.js', withSections(body || {}))
      .then(afterChange)
      .then(function (d) { return 'discount' in (body || {}) ? showRejected(d) : d; })
      .then(function (d) { busy(false); return d; }, function (err) { busy(false); emitError(err.message); return refresh(); });
  }
  function refresh() {
    return fetch(window.location.pathname + '?sections=' + sectionIds().join(','), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) { applySections(sections); emitUpdated(); return sections; })
      .catch(function (e) { emitError(e.message); });
  }
  function get() {
    return fetch(CART + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  HJ.cart = { add: add, change: change, update: update, refresh: refresh, get: get, open: function () { cartUI.open(); }, close: function () { cartUI.close(); } };

  /* ---------- forms: our own cart forms and the live site's static CartForm markup ---------- */
  function handleFromHref(href) {
    var m = /\/products\/([^/?#]+)/.exec(href || '');
    return m ? decodeURIComponent(m[1]) : null;
  }
  function handleNear(el) {
    for (var n = el, depth = 0; n && n !== document.body && depth < 10; n = n.parentElement, depth++) {
      var a = n.matches && n.matches('a[href*="/products/"]') ? n : n.querySelector && n.querySelector('a[href*="/products/"]');
      if (a) return handleFromHref(a.getAttribute('href'));
    }
    var m = /\/products\/([^/?#]+)/.exec(window.location.pathname);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function liveFormItems(form, payload) {
    var lines = (payload.inputs && payload.inputs.lines) || [];
    return lines.map(function (line) {
      var sv = line.selectedVariant || {};
      var handle = (sv.product && sv.product.handle) || handleNear(form);
      var item = { handle: handle, quantity: line.quantity || 1, options: sv.selectedOptions || [] };
      if (line.attributes && line.attributes.length) {
        item.properties = {};
        line.attributes.forEach(function (a) { item.properties[a.key] = a.value; });
      }
      return item;
    }).filter(function (i) { return i.handle; });
  }
  function withPending(el, promise) {
    el.setAttribute('data-hj-pending', '');
    var done = function () { el.removeAttribute('data-hj-pending'); };
    promise.then(done, done);
    return promise;
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    // The live site's CartForm (product cards' "+", Add Set, etc.): <form action="/cart"> + cartFormInput JSON.
    var live = form.querySelector('input[name="cartFormInput"]');
    if (live) {
      e.preventDefault();
      if (form.hasAttribute('data-hj-pending')) return;
      var payload; try { payload = JSON.parse(live.value); } catch (err) { return; }
      if (payload.action === 'LinesAdd') {
        var items = liveFormItems(form, payload);
        if (items.length) withPending(form, add(items)).catch(function () {});
      }
      return;
    }

    if (!form.hasAttribute('data-hj-cart-form')) return;
    e.preventDefault();
    if (form.hasAttribute('data-hj-pending')) return;
    var fd = new FormData(form);
    var action = form.getAttribute('action') || '';
    if (e.submitter && e.submitter.name === 'quantity') fd.set('quantity', e.submitter.value);
    if (/\/cart\/add/.test(action)) {
      var props = {};
      fd.forEach(function (v, k) { var m = /^properties\[(.+)\]$/.exec(k); if (m) props[m[1]] = v; });
      var item = { id: fd.get('id'), quantity: fd.get('quantity') || 1 };
      if (Object.keys(props).length) item.properties = props;
      if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
      withPending(form, add([item])).catch(function () {});
    } else if (/\/cart\/change/.test(action)) {
      withPending(form, change(fd.get('id'), fd.get('quantity')));
    } else if (/\/cart\/update/.test(action)) {
      var body = {};
      if (form.hasAttribute('data-hj-discount')) body.discount = String(fd.get('discount') || '').trim();
      fd.forEach(function (v, k) {
        var m = /^updates\[(.+)\]$/.exec(k);
        if (m) { body.updates = body.updates || {}; body.updates[m[1]] = Number(v) || 0; }
      });
      withPending(form, update(body));
    }
  }, true);

  // "+" quick-add buttons that are not inside a form (some static card markup): add by the card's product link.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('button[aria-label^="Add "][aria-label$=" to cart"]');
    if (!btn || btn.form) return;
    var handle = handleNear(btn);
    if (!handle) return;
    e.preventDefault();
    if (btn.hasAttribute('data-hj-pending')) return;
    withPending(btn, add([{ handle: handle, quantity: 1 }])).catch(function () {});
  });

  // Drawer close controls (also re-rendered content): close-outside, the x, links inside.
  if (cartOverlay) {
    cartOverlay.addEventListener('click', function (e) {
      var c = e.target.closest('[data-hj-close], .close-outside, header .close');
      if (c) cartUI.close();
    });
  }

  /* ---------- promo bar ---------- */
  (function promo() {
    var btn = header && $('button[aria-label^="Pause announcements"]');
    if (!btn) return;
    var bar = btn.parentElement;
    var span = $('span', bar), link = $('a', bar);
    var items = [
      { text: span ? span.textContent : 'New Arrivals Just Dropped', linkText: link ? link.textContent : '', href: link ? link.getAttribute('href') : '' },
      { text: 'Free shipping on orders over $54.99' }
    ];
    var idx = 0, paused = false, hover = false, focus = false, timer = null;
    var path = $('path', btn);
    function render() {
      var it = items[idx];
      if (span) span.textContent = it.text;
      if (link) {
        if (it.linkText) { link.textContent = it.linkText; link.setAttribute('href', it.href); link.hidden = false; }
        else link.hidden = true;
      }
      btn.setAttribute('aria-label', paused ? 'Play announcements' : 'Pause announcements (' + (idx + 1) + ' of ' + items.length + ')');
      if (path) path.setAttribute('d', paused ? 'M8 5l12 7-12 7z' : 'M7 5h4v14H7zM13 5h4v14h-4z');
    }
    function schedule() {
      clearInterval(timer); timer = null;
      if (paused || hover || focus || reducedMotion.matches) return;
      timer = setInterval(function () { idx = (idx + 1) % items.length; render(); }, 5000);
    }
    btn.addEventListener('click', function () { paused = !paused; render(); schedule(); });
    bar.addEventListener('mouseenter', function () { hover = true; schedule(); });
    bar.addEventListener('mouseleave', function () { hover = false; schedule(); });
    bar.addEventListener('focusin', function () { focus = true; schedule(); });
    bar.addEventListener('focusout', function () { focus = false; schedule(); });
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', schedule);
    render(); schedule();
  })();

  /* ---------- mega menu ---------- */
  var mega = (function () {
    var panel = $('[data-hj-mega]');
    var trigger = header && $('.sb-site-header__nav-desktop button[aria-haspopup]', header);
    var api = { open: function () {}, close: function () {} };
    if (!panel || !trigger) return api;
    var baseColor = trigger.style.color;
    var isOpen = false;
    api.open = function () {
      if (isOpen) return;
      isOpen = true;
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      trigger.style.color = 'var(--color-text, #111)';
    };
    api.close = function () {
      if (!isOpen) return;
      isOpen = false;
      var r = trigger.getBoundingClientRect();
      hoverArmed = !(pointer.x >= r.left && pointer.x <= r.right && pointer.y >= r.top && pointer.y <= r.bottom);
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      trigger.style.color = baseColor;
    };
    trigger.addEventListener('click', function () { isOpen ? api.close() : api.open(); });
    // After a close (Escape, scrim) Chrome replays a mouseenter on the trigger under a resting cursor; only a real
    // re-entry may reopen it.
    var hoverArmed = true, pointer = { x: -1, y: -1 };
    document.addEventListener('mousemove', function (e) { pointer.x = e.clientX; pointer.y = e.clientY; }, { passive: true });
    trigger.addEventListener('mouseenter', function () { if (desktop.matches && hoverArmed) api.open(); });
    trigger.addEventListener('mouseleave', function () { hoverArmed = true; });
    header.addEventListener('mouseleave', function () { if (desktop.matches) api.close(); });
    panel.addEventListener('click', function (e) {
      if (e.target.closest('.sb-site-header__mega-scrim, a')) api.close();
    });
    // Plain nav links close it (the live app closes on their click).
    $$('.sb-site-header__nav-desktop a', header).forEach(function (a) { a.addEventListener('click', api.close); });
    return api;
  })();
  HJ.mega = mega;

  /* ---------- mobile menu drawer ---------- */
  (function menu() {
    var drawer = $('[data-hj-mdrawer]');
    var burger = header && $('.sb-site-header__burger button[aria-label="Open menu"]', header);
    var ui = registerOverlay('menu', function () {
      if (drawer) drawer.hidden = false;
      if (burger) burger.setAttribute('aria-expanded', 'true');
    }, function () {
      if (drawer) drawer.hidden = true;
      if (burger) burger.setAttribute('aria-expanded', 'false');
    });
    HJ.menu = { open: ui.open, close: ui.close };
    if (burger) burger.addEventListener('click', function () { ui.open(); });
    if (drawer) drawer.addEventListener('click', function (e) {
      if (e.target.closest('.sb-site-header__drawer-scrim, button[aria-label="Close"], a')) ui.close();
    });
    // Leaving the mobile layout with the drawer open: close it (it is mobile-only on live).
    var onResize = function () { if (desktop.matches) ui.close(); };
    if (desktop.addEventListener) desktop.addEventListener('change', onResize);
  })();

  /* ---------- search overlay ---------- */
  (function search() {
    var box = $('[data-hj-search]');
    if (!box) { HJ.search = { open: function () {}, close: function () {} }; return; }
    var input = $('[data-hj-search-input]', box);
    var results = $('[data-hj-search-results]', box);
    var popularHtml = results.innerHTML;
    var LIMIT = 4;
    var seq = 0, timer = null, ctrl = null, lastTotal = 0, lastTerm = '';

    var ui = registerOverlay('search', function () {
      box.hidden = false;
      box.classList.add('expanded');
      try { input.focus({ preventScroll: true }); } catch (e) {}
    }, function () {
      box.hidden = true;
      box.classList.remove('expanded');
      input.value = '';
      input.blur();
      results.innerHTML = popularHtml;
      lastTotal = 0; lastTerm = '';
    });
    HJ.search = { open: ui.open, close: ui.close };

    var CARD_STYLE = '<style>.sc-pcard-img--hover { opacity: 0; }@media (hover: hover) and (pointer: fine) { .sc-pcard-media--swap .sc-pcard-img--hover, .sc-pcard-media--swap .sc-pcard-img--primary { transition: opacity 220ms ease; } .sc-pcard-media--swap:hover .sc-pcard-img--hover { opacity: 1; } .sc-pcard-media--swap:hover .sc-pcard-img--primary { opacity: 0; } }@media (hover: none), (pointer: coarse) { .sc-pcard-img--hover { display: none !important; } }@media (prefers-reduced-motion: reduce) { .sc-pcard-media--swap .sc-pcard-img--hover, .sc-pcard-media--swap .sc-pcard-img--primary { transition: none; } }</style>';
    var F = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
    function sized(src, w) {
      if (!src) return '';
      if (src.indexOf('//') === 0) src = 'https:' + src;
      return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
    }
    function card(p) {
      var href = ROOT + 'products/' + encodeURIComponent(p.handle);
      var img = p.featured_image && p.featured_image.url || p.image || '';
      var alt = p.featured_image && p.featured_image.alt || p.title;
      var cents = Math.round(parseFloat(p.price) * 100);
      var compare = Math.round(parseFloat(p.compare_at_price_max || 0) * 100);
      var price = compare > cents
        ? '<span style="color: var(--color-sale, #9E3B3B);">' + esc(formatMoney(cents)) + '</span> <s style="color: var(--color-muted, #9B9A97); font-weight: 400;">' + esc(formatMoney(compare)) + '</s>'
        : '<span style="color: var(--color-text, #1A1A1A);">' + esc(formatMoney(cents)) + '</span>';
      // Badges as on every product card (snippets/hj-card): Sold Out, N% OFF, designation tag, New.
      var tags = p.tags || [], lower = tags.map(function (t) { return String(t).toLowerCase(); });
      var desig = '';
      ['core', 'seasonal', 'limited'].forEach(function (w) { var i = lower.indexOf(w); if (!desig && i >= 0) desig = tags[i]; });
      var isNew = lower.indexOf('new') >= 0, onSale = compare > cents, soldOut = p.available === false;
      var B = "font-family: var(--type-eyebrow-family, " + F + "); font-size: var(--type-eyebrow-size, 10px); font-weight: var(--type-eyebrow-weight, 600); line-height: var(--type-eyebrow-line-height, 1.2); letter-spacing: 0.1em; text-transform: var(--type-eyebrow-transform, uppercase); padding: 4px 10px; border-radius: var(--radius-badge, 4px); white-space: nowrap; ";
      var badges = '';
      if (soldOut) badges = '<span style="' + B + 'background: var(--color-warm-gray, #605D51); color: var(--color-white, #FFFFFF);">Sold Out</span>';
      else {
        if (onSale) badges += '<span style="' + B + 'background: transparent; color: var(--color-sale, #9E3B3B); border: 1px solid var(--color-sale, #9E3B3B);">' + Math.round((compare - cents) * 100 / compare) + '% OFF</span>';
        if (desig) badges += '<span style="' + B + 'background: var(--color-secondary, #333333); color: var(--color-on-primary, #FFFFFF);">' + esc(desig) + '</span>';
        if (isNew) badges += '<span style="' + B + 'background: transparent; color: var(--color-secondary, #333333); border: 1px solid var(--color-warm-gray, var(--color-muted, #9B9A97));">New</span>';
      }
      var badgeStrip = badges ? '<div style="position: absolute; top: 12px; left: 12px; display: flex; gap: 6px; z-index: 2; pointer-events: none;">' + badges + '</div>' : '';
      var linkPos = badges ? 'top: var(--product-card-media-badge-strip, 0px); right: 0px; bottom: 0px; left: 0px;' : 'inset: 0px;';
      return '<div class="search-overlay-card" data-hj-handle="' + esc(p.handle) + '"><div class="sc-pcard-wrap" style="position: relative; display: flex; flex-direction: column; background: var(--color-custom-stone, #E8E6E1); border-radius: var(--radius-card, 6px); border: var(--product-card-border-width, 1px) solid var(--product-card-border-color, var(--product-card-border, var(--color-border, #E5E4E2))); box-sizing: border-box; overflow: hidden; box-shadow: rgba(26, 26, 26, 0.06) 0px 1px 3px;">' + CARD_STYLE +
        '<div class="sc-pcard-media" style="position: relative; flex-shrink: 0; overflow: hidden; background: var(--product-card-media-background, var(--product-card-media-bg, transparent)); aspect-ratio: var(--product-card-aspect, 4 / 5);"><a aria-label="' + esc(p.title) + '" href="' + href + '" style="position: absolute; ' + linkPos + ' display: block; padding: var(--product-card-media-inset, 16px); box-sizing: border-box; z-index: 1;">' +
        (img ? '<img src="' + esc(sized(img, 720)) + '" alt="' + esc(alt) + '" width="360" decoding="async" loading="lazy" class="sc-pcard-img sc-pcard-img--primary" style="width: 100%; height: 100%; object-fit: var(--product-card-image-fit, contain); display: block;">' : '') +
        '</a>' + badgeStrip + '</div><div style="padding: 16px; font-family: var(--font-body, sans-serif); flex: 1 1 0%; display: flex; flex-direction: column;"><a class="sc-pcard" href="' + href + '" style="text-decoration: none; color: inherit; display: block;">' +
        (p.type ? '<p style="font-family: var(--type-product-card-eyebrow-family, ' + F + '); font-size: var(--type-product-card-eyebrow-size, 10px); font-weight: var(--type-product-card-eyebrow-weight, 600); line-height: var(--type-product-card-eyebrow-line-height, 1.2); letter-spacing: var(--type-product-card-eyebrow-letter-spacing, 0.16em); text-transform: var(--type-product-card-eyebrow-transform, uppercase); color: var(--color-muted, #9B9A97); margin: 0px 0px 4px;">' + esc(p.type) + '</p>' : '') +
        '<p style="font-family: var(--type-product-title-family, ' + F + '); font-size: var(--type-product-title-size, 15px); font-weight: var(--type-product-title-weight, 600); line-height: var(--type-product-title-line-height, 1.3); letter-spacing: var(--type-product-title-letter-spacing, -0.01em); text-transform: var(--type-product-title-transform, none); color: var(--color-text, #1A1A1A); margin: 0px 0px 6px;">' + esc(p.title) + '</p></a>' +
        '<div style="display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-top: auto;"><p style="font-family: var(--type-body-family, ' + F + '); font-size: var(--type-body-size, 15px); font-weight: 600; line-height: var(--type-body-line-height, 1.7); letter-spacing: var(--type-body-letter-spacing, 0); text-transform: var(--type-body-transform, none); margin: 0px;">' + price + '</p></div></div></div></div>';
    }
    // Second picture for the hover swap: suggest.json only carries one image, product.js has them all.
    function addHoverImages(token) {
      $$('.search-overlay-card[data-hj-handle]', results).forEach(function (el) {
        productJSON(el.getAttribute('data-hj-handle')).then(function (p) {
          if (token !== seq) return;
          var imgs = (p.images || []);
          var first = $('.sc-pcard-img--primary', el);
          if (imgs.length < 2 || !first) return;
          var media = $('.sc-pcard-media', el), a = $('a', media);
          var hover = document.createElement('img');
          hover.src = sized(imgs[1], 720); hover.alt = ''; hover.width = 360; hover.decoding = 'async'; hover.loading = 'lazy';
          hover.className = 'sc-pcard-img sc-pcard-img--hover';
          hover.setAttribute('style', 'position: absolute; inset: var(--product-card-media-inset, 16px); width: auto; height: auto; max-width: calc(100% - 2 * var(--product-card-media-inset, 16px)); max-height: calc(100% - 2 * var(--product-card-media-inset, 16px)); margin: auto; object-fit: var(--product-card-image-fit, contain); display: block; pointer-events: none;');
          a.appendChild(hover);
          media.classList.add('sc-pcard-media--swap');
        }).catch(function () {});
      });
    }
    function viewAll(term) {
      return '<a class="search-overlay-viewall" href="' + SEARCH + '?type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(term) + '">View all results for “' + esc(term) + '” →</a>';
    }
    function run() {
      var term = input.value;
      var token = ++seq;
      if (ctrl) ctrl.abort();
      if (term.trim().length < 2) { results.innerHTML = popularHtml; lastTotal = 0; lastTerm = ''; return; }
      if (!lastTotal) results.innerHTML = '<p class="search-overlay-loading">Searching…</p>';
      ctrl = window.AbortController ? new AbortController() : null;
      var qs = 'q=' + encodeURIComponent(term) + '&resources[type]=product,collection,page,article&resources[limit]=' + LIMIT + '&resources[options][unavailable_products]=last';
      fetch(SEARCH + '/suggest.json?' + qs, { signal: ctrl ? ctrl.signal : undefined, headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (token !== seq) return;
          var res = (data && data.resources && data.resources.results) || {};
          var products = (res.products || []).slice(0, LIMIT);
          var total = ['products', 'collections', 'pages', 'articles'].reduce(function (n, k) { return n + (res[k] || []).length; }, 0);
          lastTotal = total; lastTerm = term;
          if (!total) {
            results.innerHTML = '<div class="search-overlay-empty"><p class="search-overlay-empty-sub">No results for “' + esc(term) + '” - try something else.</p></div>';
            return;
          }
          results.innerHTML = '<p class="search-overlay-label">Products</p><div class="search-overlay-products">' + products.map(card).join('') + '</div>' + viewAll(term);
          addHoverImages(token);
        })
        .catch(function (e) { if (e && e.name === 'AbortError') return; if (token === seq) results.innerHTML = '<div class="search-overlay-empty"><p class="search-overlay-empty-sub">No results for “' + esc(term) + '” - try something else.</p></div>'; });
    }
    function queue() { clearTimeout(timer); timer = setTimeout(run, 120); }
    input.addEventListener('input', queue);
    input.addEventListener('focus', function () { if (input.value.trim().length >= 2 && input.value !== lastTerm) queue(); });
    var form = $('form', box);
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var term = input.value.trim();
      if (!term) return;
      ui.close();
      window.location.href = SEARCH + '?type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(term);
    });
    box.addEventListener('click', function (e) {
      if (e.target.closest('.search-overlay-scrim, .search-overlay-close')) { ui.close(); return; }
      if (e.target.closest('a')) ui.close();
    });
    if (header) $$('button[aria-label="Search"]', header).forEach(function (b) {
      b.addEventListener('click', function () { mega.close(); ui.open(); });
    });
  })();

  /* ---------- header cart button + live-site events ---------- */
  var cb = cartButton();
  if (cb) cb.addEventListener('click', function () { mega.close(); cartUI.open(); });
  window.addEventListener('sb:open-cart', function () { cartUI.open(); });
  window.addEventListener('sb:open-search', function () { HJ.search.open(); });
  window.addEventListener('sb:cart-count-request', function () { setCount(HJ.cartCount || 0); });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (current) overlays[current.name].close();
    else mega.close();
  });

  // Initial badge from the server-rendered cart; a bfcache restore may hold a stale cart.
  var initial = cartOverlay ? cartOverlay.getAttribute('data-hj-count') : 0;
  setCount(initial);
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    if (current) overlays[current.name].close();
    mega.close();
    refresh();
  });
})();
